import React from "react"
import { Spinner } from "reactstrap"
import { Film } from "lucide-react"
import InfluencerPostCard from "./InfluencerPostCard"

/**
 * One app card from `apps[]` (INFLUENCER_PROFILE_API.md §3) - the app's own
 * catalogue info up top, that app's posts by this creator underneath, most-
 * covered app first per the API's own ordering. The app name is plain text,
 * not a link - `game_url` (e.g. "/sports/motion-soccer?id=…") doesn't match
 * this app's actual game-detail route shape (`/xr/<genre>/<slug>?id=…`, see
 * `getAppDetailsLink` in utils/seoUtils.js), so linking it sent users to the
 * home screen instead of the app.
 *
 * The response caps posts per card at whatever `videos_per_app` was
 * requested (default 12 here, see InfluencerProfileSection) - there's no
 * per-app "this card was truncated" flag, only a whole-profile
 * `videos_truncated` boolean, so this treats "returned exactly the cap" as
 * the signal that there may be more (§8's documented way to see the rest:
 * re-request with this app's `app_id` and `videos_per_app=50`) and offers
 * `onLoadAll` to do that scoped re-fetch, swapping just this card's posts.
 */
const AppCoverageCard = ({ app, requestedVideosPerApp, onLoadAll, isLoadingAll, platform, username }) => {
  const mayHaveMore = requestedVideosPerApp && app.video_count >= requestedVideosPerApp

  return (
    <div style={{ background: "#1c1d27", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "16px", padding: "18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
        {app.image_url && (
          <img src={app.image_url} alt={app.name} style={{ width: "44px", height: "44px", borderRadius: "10px", objectFit: "cover", flexShrink: 0 }} />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <span style={{ color: "#fff", fontSize: "15px", fontWeight: 600, fontFamily: "Rubik" }}>{app.name || "Unknown app"}</span>
          <div style={{ color: "#9ca3af", fontSize: "12px", marginTop: "2px" }}>
            {[app.developer, Array.isArray(app.genre) ? app.genre[0] : app.genre].filter(Boolean).join(" · ")}
          </div>
        </div>
        <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11.5px", fontWeight: 600, color: "#9ca3af", background: "rgba(255,255,255,0.05)", padding: "4px 10px", borderRadius: "999px", flexShrink: 0 }}>
          <Film size={12} /> {app.video_count} post{app.video_count === 1 ? "" : "s"}
        </span>
      </div>

      {Array.isArray(app.videos) && app.videos.length > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: "14px" }}>
          {app.videos.map((post) => (
            <InfluencerPostCard key={post.video_id || post.url} post={post} platform={platform} username={username} />
          ))}
        </div>
      ) : (
        <p style={{ color: "#6b7280", fontSize: "12.5px", margin: 0 }}>No posts about this app are stored yet.</p>
      )}

      {mayHaveMore && onLoadAll && (
        <div className="text-center" style={{ marginTop: "16px" }}>
          <button
            type="button"
            onClick={onLoadAll}
            disabled={isLoadingAll}
            style={{
              display: "inline-flex", alignItems: "center", gap: "7px",
              background: "rgba(246,37,90,0.1)", border: "1px solid rgba(246,37,90,0.3)",
              color: "#F6255A", padding: "8px 16px", borderRadius: "10px",
              fontSize: "12.5px", fontWeight: 600, fontFamily: "Rubik",
              cursor: isLoadingAll ? "default" : "pointer", opacity: isLoadingAll ? 0.6 : 1,
            }}
          >
            {isLoadingAll ? <Spinner size="sm" /> : null}
            {isLoadingAll ? "Loading…" : "See all posts about this app"}
          </button>
        </div>
      )}
    </div>
  )
}

export default AppCoverageCard
